/**
 * Aggregation helpers: turn raw portal connection records + metrics into the
 * matrix cells, summaries, and drilldowns the UI renders.
 *
 * This is pure, framework-free TypeScript. It used to run in a Cloudflare
 * Worker; it now runs directly in the browser at page-load time, so there is
 * no server to deploy or maintain.
 */

import type {
  MatrixCell,
  MatrixResponse,
  ServerSummary,
  AccountSummary,
  ServerDetail,
  AccountDetail,
  PortalAccount,
  PortalServer,
  Health,
  KpiSummary,
} from '../../../shared/types'
import type { PortalConnectionRecord } from './data'
import { getMetricsMap, metricsFor } from './prometheus'
import {
  getAllAccounts,
  getAllServers,
  getAllConnections,
} from './data'

/** Rank health worst→best for sorting (0 = worst). */
const HEALTH_RANK: Record<Health, number> = {
  blocked: 0,
  error: 1,
  degraded: 2,
  no_data: 3,
  ok: 4,
}

export function sortBySeverity(cells: MatrixCell[]): MatrixCell[] {
  return [...cells].sort(
    (a, b) => HEALTH_RANK[a.health] - HEALTH_RANK[b.health]
  )
}

/** Derive a cell's health from its connection status + metrics. */
function deriveCellHealth(
  rec: PortalConnectionRecord,
  errorRatePct: number | null
): Health {
  if (rec.is_blocked) return 'blocked'
  if (rec.connection_status === 'error') return 'error'
  if (errorRatePct !== null && errorRatePct >= 5) return 'error'
  if (errorRatePct !== null && errorRatePct >= 1) return 'degraded'
  if (rec.connection_status === 'inactive') return 'degraded'
  if (rec.connection_status === 'active') return 'ok'
  return 'no_data'
}

/** Roll a set of cell healths up into a single summary health. */
function rollupHealth(healths: Health[]): Health {
  if (healths.length === 0) return 'no_data'
  if (healths.some((h) => h === 'blocked')) return 'blocked'
  if (healths.some((h) => h === 'error')) return 'error'
  if (healths.some((h) => h === 'degraded')) return 'degraded'
  if (healths.some((h) => h === 'ok')) return 'ok'
  return 'no_data'
}

/**
 * Build the full set of matrix cells joining portal connections with metrics.
 * Central function all views reuse.
 */
export async function buildMatrix(): Promise<{
  accounts: PortalAccount[]
  servers: PortalServer[]
  cells: MatrixCell[]
}> {
  const [accounts, servers, connections, metrics] = await Promise.all([
    getAllAccounts(),
    getAllServers(),
    getAllConnections(),
    getMetricsMap(),
  ])

  const accountById = new Map(accounts.map((a) => [a.account_id, a]))
  const serverById = new Map(servers.map((s) => [s.server_id, s]))

  const cells: MatrixCell[] = []
  for (const rec of connections) {
    const acct = accountById.get(rec.account_id)
    const srv = serverById.get(rec.server_id)
    if (!acct || !srv) continue

    const m = metricsFor(metrics, rec.account_id, rec.server_id)
    const health = deriveCellHealth(rec, m.error_rate_pct)

    cells.push({
      account_id: acct.account_id,
      account_name: acct.account_name,
      server_id: srv.server_id,
      server_name: srv.server_name,
      provider: srv.provider,
      connection_status: rec.connection_status,
      is_blocked: rec.is_blocked,
      error_rate_pct: m.error_rate_pct,
      requests_per_min: m.requests_per_min,
      avg_latency_ms: m.avg_latency_ms,
      issue_count: rec.issue_count,
      last_seen: rec.last_seen,
      health,
    })
  }

  return { accounts, servers, cells }
}

export async function buildMatrixResponse(): Promise<MatrixResponse> {
  const { accounts, servers, cells } = await buildMatrix()
  return {
    accounts,
    servers,
    cells,
    generated_at: new Date().toISOString(),
  }
}

export function summarizeServer(
  server: PortalServer,
  cells: MatrixCell[]
): ServerSummary {
  const mine = cells.filter((c) => c.server_id === server.server_id)
  return {
    server_id: server.server_id,
    server_name: server.server_name,
    provider: server.provider,
    total_connected_accounts: mine.length,
    blocked_accounts: mine.filter((c) => c.is_blocked).length,
    error_accounts: mine.filter((c) => c.health === 'error').length,
    health: rollupHealth(mine.map((c) => c.health)),
  }
}

export function summarizeAccount(
  account: PortalAccount,
  cells: MatrixCell[]
): AccountSummary {
  const mine = cells.filter((c) => c.account_id === account.account_id)
  return {
    account_id: account.account_id,
    account_name: account.account_name,
    total_connected_servers: mine.length,
    blocked_servers: mine.filter((c) => c.is_blocked).length,
    error_servers: mine.filter((c) => c.health === 'error').length,
    health: rollupHealth(mine.map((c) => c.health)),
  }
}

export async function buildServerDetail(
  serverId: string
): Promise<ServerDetail | null> {
  const { servers, cells } = await buildMatrix()
  const server = servers.find((s) => s.server_id === serverId)
  if (!server) return null

  const mine = sortBySeverity(cells.filter((c) => c.server_id === serverId))

  const issueMap = new Map<string, number>()
  for (const c of mine) {
    if (c.is_blocked) issueMap.set('Blocked', (issueMap.get('Blocked') ?? 0) + 1)
    if (c.connection_status === 'error')
      issueMap.set('Connection Error', (issueMap.get('Connection Error') ?? 0) + 1)
    if (c.connection_status === 'inactive')
      issueMap.set('Inactive', (issueMap.get('Inactive') ?? 0) + 1)
  }

  return {
    server,
    summary: summarizeServer(server, cells),
    affected_accounts: mine,
    issues_by_type: [...issueMap.entries()]
      .map(([issue_type, count]) => ({ issue_type, count }))
      .sort((a, b) => b.count - a.count),
  }
}

export async function buildAccountDetail(
  accountId: string
): Promise<AccountDetail | null> {
  const { accounts, cells } = await buildMatrix()
  const account = accounts.find((a) => a.account_id === accountId)
  if (!account) return null

  const mine = sortBySeverity(cells.filter((c) => c.account_id === accountId))

  return {
    account,
    summary: summarizeAccount(account, cells),
    connected_servers: mine,
  }
}

export async function buildKpiSummary(): Promise<KpiSummary> {
  const { accounts, servers, cells } = await buildMatrix()
  const blocked = cells.filter((c) => c.is_blocked)
  const affectedAccounts = new Set(
    cells.filter((c) => c.health === 'blocked' || c.health === 'error').map((c) => c.account_id)
  )
  const affectedServers = new Set(
    cells.filter((c) => c.health === 'blocked' || c.health === 'error').map((c) => c.server_id)
  )
  return {
    total_accounts: accounts.length,
    total_servers: servers.length,
    total_connections: cells.length,
    blocked_connections: blocked.length,
    affected_accounts: affectedAccounts.size,
    affected_servers: affectedServers.size,
  }
}
